// React
import { Fragment, useContext, useEffect } from "react"

// Components
import { ActionButton, Button, NumberInput } from "components"
import { DetailProduct } from "pages/Purchase/components"
import { DeleteAlert } from "pages/Purchase/Transaction/components"
import { Description, ItemOrder, Job, Price, Tax, Total, Unit } from "./components"

// Contexts
import { DataJournalContext } from "contexts"
import { ProductContext } from "./contexts"
import { PurchaseOrderContext } from "../../contexts"

// Form
import { Controller, FormProvider, useFieldArray, useForm, useFormContext, useWatch } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"

// Icons
import { TbEye } from "react-icons/tb"

// Third-Party Libraries
import toast from "react-hot-toast"
import { useLocation } from "react-router-dom"

// Types
import type { ProductType } from "./types"

// Utils
import { APPROVAL, convertNumber, useToggle } from "utils"
import { type DetailFormType, type FormType, detail_validation_schema } from "../../utils"
import { useProduct } from "./utils"

export function DetailBody() {
  // Hooks
  const location = useLocation()
  const stateData = useToggle(Boolean(location.state))
  const { data, isLoading, refetch } = useProduct()
  const { disabled } = useContext(PurchaseOrderContext)

  // Form
  const { control } = useFormContext<FormType>()
  const vendor_id = useWatch({
    control,
    name: "vendor_id"
  })
  const { fields, append, remove } = useFieldArray({
    control,
    name: "podetail"
  })

  useEffect(() => {
    if (vendor_id) {
      refetch(vendor_id)
    }

    // eslint-disable-next-line
  }, [vendor_id])


  return (
    <ProductContext.Provider value={{ data, isLoading }}>
      <Fragment>
        {fields.map((field, index) => (
          <ListSection
            key={field.id}  
            index={index}
            onDelete={() => remove(index)}
          />
        ))}

        {!disabled && (
          <FormSection
            getFromState={stateData.isActive}
            onCreate={(value) => {
              if (fields.find(item => item.product_id === value.product_id)) {
                return toast.error("The product has already on the detail list")
              }

              append(value)

              if (stateData.isActive) {
                stateData.setActive(false)
              }
            }}
          />
        )}
      </Fragment>
    </ProductContext.Provider>
  )
}

function FormSection(params: {
  getFromState?: boolean
  default_values?: DetailFormType
  onCancel?: () => void
  onCreate?: (value: DetailFormType) => void
  onUpdate?: (value: DetailFormType) => void
}) {
  const getFromState = params.getFromState
  const location = useLocation()
  const state = location.state
  const { isActive, toggle } = useToggle(getFromState ? Boolean(getFromState) : Boolean(params.default_values))

  const { watch } = useFormContext<FormType>()
  const isView = watch("approval_status") === APPROVAL.APPROVED || watch("approval_status") === APPROVAL.REJECTED

  // const vendor_id = useWatch({
  //   control,
  //   name: "vendor_id"
  // })


  const methods = useForm<DetailFormType>({
    defaultValues: params.default_values ?? {
      delivered: 0,
      discount: 0,
      mr_unit_id: "",
      price_measure: 0,
      product_id: "",
      project_id: "",
      qty_order: 0,
      tax_id: "",
      total: 0,
      description: "",
    },
    resolver: yupResolver(detail_validation_schema)
  })
  const onCreate = (value: DetailFormType) => {
    if (params.onCreate) {
      methods.reset()
      toggle()

      params.onCreate(value)
    }
  }
  const onUpdate = (value: DetailFormType) => {
    if (params.onUpdate) {
      params.onUpdate(value)
    }
  }

  // useEffect(() => {
  //   if (vendor_id) {
  //     methods.reset()
  //   }

  //   // eslint-disable-next-line
  // }, [vendor_id])


  useEffect(() => {
    if (getFromState && state && typeof state?.qty_order === "number") {
      setTimeout(() => {
        methods.setValue("qty_order", state.qty_order)
      }, 1000)
    }

    // eslint-disable-next-line
  }, [state, getFromState])

  if (isActive) {
    return (
      <FormProvider {...methods}>
        <tr className="no-padding-body">
          <td />

          <td className="min-w-[200px]">
            <ItemOrder getFromState={Boolean(getFromState)} />
          </td>

          <td className="min-w-[200px] !px-3">
            <Description />
          </td>

          <Controller
            name="qty_order"
            control={methods.control}
            render={({ field, fieldState }) => (
              <td className="min-w-[200px]">
                <NumberInput
                  error={fieldState.error?.message}
                  ref={field.ref}
                  value={field.value}
                  onValueChange={e => field.onChange(e.floatValue)}
                />
              </td>
            )}
          />

          <td className="min-w-[200px]">
            <div className="px-3">{methods.getValues("delivered")}</div>
          </td>

          <Backorder />

          <td className="min-w-[200px] !px-3">
            <Unit />
          </td>

          <td className="min-w-[200px]">
            <Price />
          </td>

          <td className="min-w-[200px]">
            <Controller
              name="discount"
              control={methods.control}
              render={({ field, fieldState }) => (
                <NumberInput
                  textRight
                  suffix="%"
                  ref={field.ref}
                  value={field.value}
                  onValueChange={(value) => field.onChange(value.floatValue)}
                  error={fieldState.error?.message}
                />
              )}
            />
          </td>

          <td className="min-w-[200px]">
            <Total />
          </td>

          <td className="min-w-[200px]">
            <Job />
          </td>

          <td className="min-w-[200px]">
            <Tax />
          </td>

          <td className="text-center">
            {params.default_values ? (
              <section className="flex justify-center">
                <ActionButton.Check
                  type="button"
                  onClick={methods.handleSubmit(onUpdate, (errors) => {
                    console.error("FORM VALIDATION ERROR:", errors)
                  })}
                />

                <ActionButton.Close
                  type="button"
                  onClick={params.onCancel}
                />
              </section>
            ) : (
              <ActionButton.Add
                type="button"
                onClick={methods.handleSubmit(onCreate)}
              />
            )}
          </td>
        </tr>
      </FormProvider>
    )
  }

  return (
    <tr className="no-padding-body">
      <td className="text-center">
        {!isView && (
          <Button color="transparent" onClick={toggle}>
            <TbEye className="size-5" />
          </Button>
        )}
      </td>

      <td colSpan={12} />
    </tr>
  )
}

function Backorder() {
  // Form
  const { control, getValues } = useFormContext<DetailFormType>()
  const qty_order = useWatch({
    control,
    name: "qty_order"
  })

  return (
    <td className="min-w-[100px]">
      <div className="px-3">
        {convertNumber(qty_order - getValues("delivered")).intoNormalAmount}
      </div>
    </td>
  )
}

function ListSection(params: {
  index: number
  onDelete: () => void
}) {
  // Hooks
  const { data } = useContext(ProductContext)
  const { disabled } = useContext(PurchaseOrderContext)
  const { isActive, toggle } = useToggle(false)
  const { project, tax_code } = useContext(DataJournalContext)

  // Form
  const { control, getValues, watch } = useFormContext<FormType>()
  const isView = watch("approval_status") === APPROVAL.APPROVED || watch("approval_status") === APPROVAL.REJECTED

  return (
    <Controller
      name={`podetail.${params.index}` as const}
      control={control}
      render={({ field }) => {
        // Vars
        const item_order: ProductType | undefined = data.find(item => item.value === field.value.product_id)
        const project_name: string | undefined = project.data.find(item => item.value === field.value.project_id)?.label
        const tax_name: string | undefined = tax_code.data.find(item => item.value === field.value.tax_id)?.label

        if (isActive) {
          return (
            <FormSection
              default_values={field.value}
              onCancel={toggle}
              onUpdate={res => {
                if (res.product_id !== field.value.product_id && getValues("podetail").find(item => item.product_id === res.product_id)) {
                  return toast.error("The product has already on the detail list")
                }

                field.onChange(res)
                toggle()
              }}
            />
          )
        }

        return (
          <tr>
            <td>{params.index + 1}</td>

            <td>
              <DetailProduct
                barcode={item_order?.product_barcode ?? ""}
                id={field.value.product_id}
              />
            </td>

            <td>{field.value.description ?? field.value.vendor_description ?? ''}</td>
            <td>{convertNumber(field.value.qty_order).intoNormalAmount}</td>
            <td>{convertNumber(field.value.delivered).intoNormalAmount}</td>
            <td>{convertNumber(field.value.qty_order - field.value.delivered).intoNormalAmount}</td>
            <td>{item_order?.mr_unit_name}</td>
            <td className="text-right">{convertNumber(field.value.price_measure).intoCurrency}</td>
            <td className="text-right">{convertNumber(field.value.discount).intoNormalAmount}%</td>
            <td className="text-right">{convertNumber(field.value.total).intoCurrency}</td>
            <td>{project_name}</td>
            <td>{tax_name}</td>
            <td>
              {!disabled && !isView && (
                <section className="flex justify-center">
                  <ActionButton.Update
                    type="button"
                    onClick={toggle}
                  />

                  <DeleteAlert onDelete={params.onDelete} />
                </section>
              )}
            </td>
          </tr>
        )
      }}
    />
  )
}